/*
 *  -----------------------------------------------------------------------
 *    <copyright file="generated-reports-grid.helper.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { ExportExcelComponentInputColumn } from '@components/core/export-excel';
import { LabelStatuses } from '@components/core/status-label/status-label.interface';
import { GeneratedReport, GeneratedReportStatus } from '@interfaces/report-templates.interface';
import { AuditServiceType } from 'types/graphql-global-types';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { dataSets_OpraQueries_dataSets_DataSet } from '@queries/generated/central-monitoring.generated';

export const getGeneratedReportsGridData = (
    reports: GeneratedReport[] | undefined,
    datasets: (dataSets_OpraQueries_dataSets_DataSet | null)[] | null | undefined,
    prefixes?: auditRecordIdPrefixes['auditRecordIdPrefixes'],
) => {
    if (!reports) {
        return [];
    }

    const prefix = prefixes?.find((p) => p?.service === AuditServiceType.REPORTING)?.prefix;

    return reports.map((report) => {
        const datasetValue = datasets?.find((dataset) => dataset?.id === report.datasetId);
        const comparisonDatasetValue = datasets?.find((dataset) => dataset?.id === report.comparisonDatasetId);
        return {
            ...report,
            fullName: report.includeInternalRisks ? `INTERNAL ${report.reportName}` : report.reportName,
            dataset: datasetValue ? datasetValue.dataCutDate : '',
            _dataset: datasetValue ? stringDateWithoutTimeFromUtc(datasetValue.dataCutDate, 'DD-MMM-YYYY') : '',
            comparisonDataset: comparisonDatasetValue ? comparisonDatasetValue.dataCutDate : '',
            _comparisonDataset: comparisonDatasetValue
                ? stringDateWithoutTimeFromUtc(comparisonDatasetValue.dataCutDate, 'DD-MMM-YYYY')
                : '',
            _status: GeneratedReportStatus[report.status!],
            _createdDate: stringDateWithoutTimeFromUtc(report.createdDate!, 'DD-MMM-YYYY HH:mm:ss'),
            _itemId: `${prefix}${report.recordId}`,
        };
    });
};

export const reportStatusToLabelStatus = (status: GeneratedReportStatus): LabelStatuses => {
    switch (status) {
        case GeneratedReportStatus.Ready:
            return LabelStatuses.success;
        case GeneratedReportStatus.Obsolete:
            return LabelStatuses.default;
        case GeneratedReportStatus.Queued:
            return LabelStatuses.notice;
        case GeneratedReportStatus.Failed:
            return LabelStatuses.failure;
        default:
            return LabelStatuses.default;
    }
};

export const mapReportColumns = (inputColumns: ExportExcelComponentInputColumn[]) =>
    inputColumns
        .filter((x) => x.show === true)
        .map(
            (column) =>
                ({
                    title: column.label ?? column.title,
                    field: ['dataset', 'comparisonDataset', 'createdDate'].includes(column.field)
                        ? `_${column.field}`
                        : column.field,
                }) as ExportExcelComponentInputColumn,
        );
